/**
 * Liga as camadas vetoriais a um mapa OpenLayers.
 *
 * Cada camada de mapa (ex.: "Terras indígenas") vira algumas subcamadas OL
 * que compartilham a mesma `VectorSource`:
 * - preenchimento  → Z.VECTOR_FILL + n  (VectorImageLayer)
 * - contorno/linha → Z.VECTOR_LINE + n  (VectorImageLayer; carrega o popup)
 * - rótulos        → Z.LABELS + n       (VectorLayer com declutter comum)
 *
 * As camadas só são criadas depois que os dados chegam e depois ficam no
 * mapa (desligar = `setVisible(false)`). Mudança de estilo só troca a função
 * de estilo das subcamadas afetadas — nada é recriado.
 */
import type { ReactNode } from 'react';
import type OlMap from 'ol/Map';
import type { FeatureLike } from 'ol/Feature';
import VectorLayer from 'ol/layer/Vector';
import VectorImageLayer from 'ol/layer/VectorImage';
import VectorSource from 'ol/source/Vector';
import type { OrderFunction } from 'ol/render';
import type { StyleLike } from 'ol/style/Style';
import { Z } from '../map/zIndex';
import { registerGoesProjection } from '../map/projection';
import { VECTOR_LAYERS } from './catalog';
import { attachHighlightSource } from './highlight';
import { ensureDataSource, getLoadedSource } from './loader';
import {
  areaRenderOrder,
  casedLineStyles,
  highlightStyles,
  polygonFillStyle,
  polygonLabelStyleFunction,
  polygonOutlineStyle,
  regionOutlineStyles,
  riosLabelStyleFunction,
  riosLineStyleFunction,
  rodoviasLabelStyleFunction,
  rodoviasLineStyleFunction,
  sedesRenderOrder,
  sedesStyleFunction,
  ucFillStyleFunction,
  ucOutlineStyleFunction,
} from './olStyles';
import {
  municipioPopup,
  regionPopup,
  rioPopup,
  rodoviaPopup,
  terraIndigenaPopup,
  unidadeConservacaoPopup,
} from './popups';
import { selectStyle, type VectorState } from './store';
import { sameStyle } from './styleConfig';
import type { DataLayerId, LayerStyle, RegionLayerId, StyleKey, VectorLayerId } from './types';

/** Grupo de declutter comum a todos os rótulos deste módulo. */
export const DECLUTTER_GROUP = 'amaview-vectors';

/** Propriedade gravada em todas as camadas OL deste módulo. */
export const MODULE_TAG = 'vectors';

type PopupFn = (feature: FeatureLike) => ReactNode | null;

interface SubLayerSpec {
  part: string;
  data: DataLayerId;
  /** `image` = VectorImageLayer (polígonos/linhas pesados); `vector` = VectorLayer. */
  kind: 'image' | 'vector';
  z: number;
  declutter?: boolean;
  renderOrder?: OrderFunction;
  /** Campos de estilo que afetam esta subcamada. */
  keys: readonly StyleKey[];
  style: (s: LayerStyle) => StyleLike;
  /** Visibilidade extra conforme o estilo (ex.: preenchimento desligado). */
  visible?: (s: LayerStyle) => boolean;
  popup?: PopupFn;
}

const LABEL_KEYS = ['labels', 'labelSize', 'labelColor'] as const;

/**
 * Sub-regiões: divisas abaixo dos contornos dos municípios (o clique num
 * município continua mostrando o município) e nomes abaixo dos das cidades.
 * `order` separa as camadas entre si (regiões maiores por cima).
 */
function regionSublayers(id: RegionLayerId, order: number): SubLayerSpec[] {
  return [
    {
      part: 'fill',
      data: id,
      kind: 'image',
      z: Z.VECTOR_FILL - 5 + order,
      keys: ['fillColor', 'fillOpacity'],
      style: polygonFillStyle,
      visible: (s) => s.fill,
    },
    {
      part: 'line',
      data: id,
      kind: 'image',
      z: Z.VECTOR_LINE - 5 + order,
      keys: ['strokeColor', 'strokeWidth'],
      style: regionOutlineStyles,
      popup: regionPopup(id),
    },
    {
      part: 'label',
      data: id,
      kind: 'vector',
      z: Z.LABELS - 5 + order,
      declutter: true,
      renderOrder: areaRenderOrder,
      keys: LABEL_KEYS,
      style: (s) => polygonLabelStyleFunction(s, { prop: 'nome', weight: 600, italic: true, wrap: 16, minPx: 60 }),
      visible: (s) => s.labels,
    },
  ];
}

/** Receita de subcamadas de cada camada de mapa. */
export const SUBLAYERS: Record<VectorLayerId, readonly SubLayerSpec[]> = {
  municipios: [
    {
      part: 'fill',
      data: 'municipios',
      kind: 'image',
      z: Z.VECTOR_FILL,
      keys: ['fillColor', 'fillOpacity'],
      style: polygonFillStyle,
      visible: (s) => s.fill,
    },
    {
      part: 'line',
      data: 'municipios',
      kind: 'image',
      z: Z.VECTOR_LINE,
      keys: ['strokeColor', 'strokeWidth'],
      style: polygonOutlineStyle,
      popup: municipioPopup,
    },
  ],
  sedes: [
    {
      part: 'label',
      data: 'sedes',
      kind: 'vector',
      z: Z.LABELS + 5,
      declutter: true,
      renderOrder: sedesRenderOrder,
      keys: ['labels', 'labelSize', 'labelColor', 'points', 'strokeColor', 'density'],
      style: sedesStyleFunction,
      visible: (s) => s.labels || s.points,
    },
  ],
  estados: [
    {
      part: 'line',
      data: 'estados',
      kind: 'vector',
      z: Z.VECTOR_LINE + 5,
      keys: ['strokeColor', 'strokeWidth'],
      style: (s) => casedLineStyles(s.strokeColor, s.strokeWidth),
    },
    {
      part: 'amazonia',
      data: 'amazonia_legal',
      kind: 'vector',
      z: Z.VECTOR_LINE + 6,
      keys: ['altColor', 'strokeWidth'],
      style: (s) => casedLineStyles(s.altColor, Math.round((s.strokeWidth + 0.8) * 10) / 10),
      visible: (s) => s.altOn,
    },
    {
      part: 'label',
      data: 'estados',
      kind: 'vector',
      // Acima dos nomes das cidades: só 9 siglas, e o usuário as ligou de propósito.
      z: Z.LABELS + 6,
      declutter: true,
      keys: LABEL_KEYS,
      style: (s) => polygonLabelStyleFunction(s, { prop: 'sigla', weight: 700 }),
      visible: (s) => s.labels,
    },
  ],
  regioes_imediatas: regionSublayers('regioes_imediatas', 0),
  microrregioes: regionSublayers('microrregioes', 1),
  regioes_intermediarias: regionSublayers('regioes_intermediarias', 2),
  mesorregioes: regionSublayers('mesorregioes', 3),
  regioes_integracao_pa: regionSublayers('regioes_integracao_pa', 4),
  terras_indigenas: [
    {
      part: 'fill',
      data: 'terras_indigenas',
      kind: 'image',
      z: Z.VECTOR_FILL + 2,
      keys: ['fillColor', 'fillOpacity'],
      style: polygonFillStyle,
      visible: (s) => s.fill,
    },
    {
      part: 'line',
      data: 'terras_indigenas',
      kind: 'image',
      z: Z.VECTOR_LINE + 1,
      keys: ['strokeColor', 'strokeWidth'],
      style: polygonOutlineStyle,
      popup: terraIndigenaPopup,
    },
    {
      part: 'label',
      data: 'terras_indigenas',
      kind: 'vector',
      z: Z.LABELS + 3,
      declutter: true,
      renderOrder: areaRenderOrder,
      keys: LABEL_KEYS,
      style: (s) => polygonLabelStyleFunction(s, { prop: 'nome', weight: 600, wrap: 18, minPx: 36 }),
      visible: (s) => s.labels,
    },
  ],
  unidades_conservacao: [
    {
      part: 'fill',
      data: 'unidades_conservacao',
      kind: 'image',
      z: Z.VECTOR_FILL + 1,
      keys: ['strokeColor', 'altColor', 'fillOpacity'],
      style: ucFillStyleFunction,
      visible: (s) => s.fill,
    },
    {
      part: 'line',
      data: 'unidades_conservacao',
      kind: 'image',
      z: Z.VECTOR_LINE + 2,
      keys: ['strokeColor', 'altColor', 'strokeWidth'],
      style: ucOutlineStyleFunction,
      popup: unidadeConservacaoPopup,
    },
    {
      part: 'label',
      data: 'unidades_conservacao',
      kind: 'vector',
      z: Z.LABELS + 2,
      declutter: true,
      renderOrder: areaRenderOrder,
      keys: LABEL_KEYS,
      style: (s) => polygonLabelStyleFunction(s, { prop: 'nome', weight: 600, italic: true, wrap: 18, minPx: 36 }),
      visible: (s) => s.labels,
    },
  ],
  rios: [
    {
      part: 'line',
      data: 'rios',
      kind: 'image',
      z: Z.VECTOR_LINE + 3,
      keys: ['strokeColor', 'strokeWidth'],
      style: riosLineStyleFunction,
      popup: rioPopup,
    },
    {
      part: 'label',
      data: 'rios',
      kind: 'vector',
      z: Z.LABELS,
      declutter: true,
      keys: LABEL_KEYS,
      style: riosLabelStyleFunction,
      visible: (s) => s.labels,
    },
  ],
  rodovias: [
    {
      part: 'line',
      data: 'rodovias',
      kind: 'image',
      z: Z.VECTOR_LINE + 4,
      keys: ['strokeColor', 'strokeWidth'],
      style: rodoviasLineStyleFunction,
      popup: rodoviaPopup,
    },
    {
      part: 'label',
      data: 'rodovias',
      kind: 'vector',
      z: Z.LABELS + 1,
      declutter: true,
      keys: [...LABEL_KEYS, 'altColor'],
      style: rodoviasLabelStyleFunction,
      visible: (s) => s.labels,
    },
  ],
};

type AnyVectorLayer = VectorLayer<VectorSource> | VectorImageLayer<VectorSource>;

interface SubLayer {
  spec: SubLayerSpec;
  layer: AnyVectorLayer;
}

function createSubLayer(id: VectorLayerId, spec: SubLayerSpec, source: VectorSource): AnyVectorLayer {
  const properties = { module: MODULE_TAG, vectorLayer: id, part: spec.part };
  const layer =
    spec.kind === 'image'
      ? new VectorImageLayer({ source, properties, imageRatio: 1.25, visible: false })
      : new VectorLayer({
          source,
          properties,
          visible: false,
          declutter: spec.declutter ? DECLUTTER_GROUP : false,
          renderOrder: spec.renderOrder,
        });
  layer.setZIndex(spec.z);
  if (spec.popup) layer.set('popup', spec.popup);
  return layer;
}

/** Subcamadas de uma camada de mapa já adicionadas ao mapa. */
class LayerBundle {
  readonly subs: SubLayer[];
  /** Arquivos de dados disponíveis quando o conjunto foi montado. */
  readonly dataIds: ReadonlySet<DataLayerId>;
  private applied: LayerStyle | null = null;

  constructor(
    readonly id: VectorLayerId,
    private readonly map: OlMap,
  ) {
    this.subs = [];
    const ids = new Set<DataLayerId>();
    for (const spec of SUBLAYERS[id]) {
      const source = getLoadedSource(spec.data);
      if (!source) continue;
      ids.add(spec.data);
      const layer = createSubLayer(id, spec, source);
      this.subs.push({ spec, layer });
      map.addLayer(layer);
    }
    this.dataIds = ids;
  }

  update(style: LayerStyle, visible: boolean): void {
    for (const { spec, layer } of this.subs) {
      if (!this.applied || !sameStyle(this.applied, style, spec.keys)) {
        layer.setStyle(spec.style(style));
      }
      if (layer.getOpacity() !== style.opacity) layer.setOpacity(style.opacity);
      const v = visible && (spec.visible ? spec.visible(style) : true);
      if (layer.getVisible() !== v) layer.setVisible(v);
    }
    this.applied = style;
  }

  dispose(): void {
    for (const { layer } of this.subs) this.map.removeLayer(layer);
    this.subs.length = 0;
  }
}

/** Gerencia as camadas vetoriais de um mapa. Um por mapa (ver useVectorLayers). */
export class VectorLayerManager {
  private readonly bundles = new Map<VectorLayerId, LayerBundle>();
  private readonly highlightSource = new VectorSource({ wrapX: false });
  private readonly highlightLayer: VectorLayer<VectorSource>;
  private disposed = false;

  constructor(private readonly map: OlMap) {
    registerGoesProjection();
    this.highlightLayer = new VectorLayer({
      source: this.highlightSource,
      style: highlightStyles(),
      properties: { module: MODULE_TAG, part: 'highlight' },
    });
    this.highlightLayer.setZIndex(Z.HIGHLIGHT);
    map.addLayer(this.highlightLayer);
    attachHighlightSource(this.highlightSource);
  }

  /** Aplica o estado do store ao mapa (idempotente). */
  sync(state: VectorState): void {
    if (this.disposed) return;
    if (!state.highlight) this.highlightSource.clear();
    for (const def of VECTOR_LAYERS) {
      const visible = state.visible[def.id];
      let bundle = this.bundles.get(def.id);

      if (visible) {
        // Pede os arquivos que faltam (carregamento sob demanda).
        let pending = false;
        for (const d of def.data) {
          const st = state.data[d]?.status ?? 'idle';
          if (st === 'idle') {
            void ensureDataSource(d);
            pending = true;
          } else if (st === 'loading') pending = true;
        }
        // Remonta se chegou um arquivo que faltava (ex.: após nova tentativa).
        if (bundle && def.data.some((d) => !bundle!.dataIds.has(d) && getLoadedSource(d))) {
          bundle.dispose();
          this.bundles.delete(def.id);
          bundle = undefined;
        }
        if (!bundle && !pending && def.data.some((d) => getLoadedSource(d))) {
          bundle = new LayerBundle(def.id, this.map);
          this.bundles.set(def.id, bundle);
        }
      }

      bundle?.update(selectStyle(state, def.id), visible);
    }
  }

  /** Camadas OL criadas até agora (para depuração/testes). */
  getLayers(): AnyVectorLayer[] {
    return [...this.bundles.values()].flatMap((b) => b.subs.map((s) => s.layer));
  }

  dispose(): void {
    this.disposed = true;
    for (const b of this.bundles.values()) b.dispose();
    this.bundles.clear();
    this.map.removeLayer(this.highlightLayer);
    attachHighlightSource(null);
  }
}
